import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Menu as MenuIcon, ArrowUp, ArrowDown, ExternalLink, Loader2, GripVertical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuEditor = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    link: '',
    ativo: true
  });

  useEffect(() => {
    loadMenu();
  }, []);

  const loadMenu = async () => {
    try {
      const response = await axios.get(`${API}/admin/menu`, {
        headers: getAuthHeaders()
      });
      setMenuItems(response.data.items || []);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar menu',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveMenu = async (items) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/menu`, { items }, {
        headers: getAuthHeaders()
      });
      toast({ title: 'Menu guardado com sucesso!' });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao guardar menu',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    if (!formData.label || !formData.link) {
      toast({
        title: 'Erro',
        description: 'Preencha todos os campos',
        variant: 'destructive'
      });
      return;
    }

    const newItem = {
      id: Date.now().toString(),
      label: formData.label,
      link: formData.link,
      ordem: menuItems.length,
      ativo: formData.ativo
    };

    const updatedItems = editingItem
      ? menuItems.map(item => item.id === editingItem.id ? { ...newItem, id: editingItem.id } : item)
      : [...menuItems, newItem];

    setMenuItems(updatedItems);
    saveMenu(updatedItems);
    setDialogOpen(false);
    resetForm();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      label: item.label,
      link: item.link,
      ativo: item.ativo !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este item?')) return;
    
    const updatedItems = menuItems.filter(item => item.id !== id);
    setMenuItems(updatedItems);
    saveMenu(updatedItems);
  };

  const moveItem = (index, direction) => {
    const newItems = [...menuItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newItems.length) return;
    
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    newItems.forEach((item, i) => item.ordem = i);
    
    setMenuItems(newItems);
    saveMenu(newItems);
  };

  const toggleActive = (id) => {
    const updatedItems = menuItems.map(item => 
      item.id === id ? { ...item, ativo: !item.ativo } : item
    );
    setMenuItems(updatedItems);
    saveMenu(updatedItems);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      label: '',
      link: '',
      ativo: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#cc8b3a] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Menu de Navegação</h1>
          <p className="text-white/60">Configure os links do menu principal do site</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold">
              <Plus className="w-4 h-4 mr-2" />
              Novo Item
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? 'Editar Item' : 'Novo Item do Menu'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Texto do Link</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="Ex: Produtos"
                />
              </div>
              <div className="space-y-2">
                <Label>URL / Caminho</Label>
                <Input
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="Ex: /produtos ou https://..."
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.ativo}
                  onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
                />
                <Label>Item Ativo</Label>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-[#cc8b3a]/30 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddItem}
                  className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black"
                >
                  {editingItem ? 'Atualizar' : 'Adicionar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <MenuIcon className="w-5 h-5 mr-2 text-[#cc8b3a]" />
            Itens do Menu
          </CardTitle>
        </CardHeader>
        <CardContent>
          {menuItems.length === 0 ? (
            <p className="text-white/60 text-center py-8">
              Nenhum item no menu. Clique em "Novo Item" para começar.
            </p>
          ) : (
            <div className="space-y-2">
              {menuItems.sort((a, b) => a.ordem - b.ordem).map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    item.ativo ? 'bg-black/30 border-[#cc8b3a]/30' : 'bg-black/10 border-gray-700 opacity-50'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <GripVertical className="w-5 h-5 text-white/30" />
                    <div>
                      <p className="text-white font-medium">{item.label}</p>
                      <p className="text-white/50 text-sm flex items-center">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        {item.link}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="text-white/60 hover:text-white"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === menuItems.length - 1}
                      className="text-white/60 hover:text-white"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Switch
                      checked={item.ativo !== false}
                      onCheckedChange={() => toggleActive(item.id)}
                    />
                    <Button
                      size="sm"
                      onClick={() => handleEdit(item)}
                      className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(item.id)}
                      className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-[#cc8b3a] text-black px-4 py-2 rounded-lg flex items-center">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          A guardar...
        </div>
      )}
    </div>
  );
};

export default MenuEditor;
